import { create } from "zustand";
import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";
import { parseThemeValues } from "@theme/shared/theme-values";

/**
 * Tenant context store.
 *
 * On app startup the SPA calls the anonymous `GET /auth/tenant/context` once to
 * learn the tenant's name, branding `theme`, logo and `authenticationMode`. That
 * drives (a) the app theme colours (ThemeContext), (b) the logo/name shown on
 * the startup/login screen and top nav, and (c) whether the login page shows the
 * sign-in form (Local) or begins the ActiveDirectory flow.
 *
 * The last-known context is cached to sessionStorage and used to SEED the store
 * synchronously on startup, so the first paint already has the tenant's colours
 * — without it the app renders the default theme for a frame and then flips to
 * the tenant theme once the fetch lands (a visible flash on every reload). The
 * async fetch then refreshes the cache.
 *
 * The fetch is fail-soft: `ensureLoaded` fetches at most once (deduped); on
 * error it keeps whatever was seeded from cache (falling back to the Oxygen
 * defaults only when there is no cache). `refresh` forces a re-fetch after an
 * admin edits the tenant, so the live app updates without a reload.
 */

const STORAGE_KEY = "tenantContext";

const readCache = () => {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore quota / unavailable storage — caching is a best-effort optimisation.
  }
};

const toFields = (data) => ({
  name: data?.name ?? null,
  theme: data?.theme ?? null,
  logoUrl: data?.logoUrl ?? null,
  mediaUrl: data?.mediaUrl ?? null,
  authenticationMode: data?.authenticationMode ?? null,
});

// Seed synchronously from the cached context so the first render is themed.
// `status` stays "idle" (not "ready") so ensureLoaded still fetches fresh data.
const initialState = { status: "idle", ...toFields(readCache()) };

// Dedupe guard for in-flight requests — kept outside the store so concurrent
// callers share the same promise without an extra render.
let inflight = null;

const applyContext = (set, data) => {
  const fields = toFields(data);
  writeCache(fields);
  set({ status: "ready", ...fields });
};

const useTenantContextStore = create((set, get) => ({
  ...initialState,

  ensureLoaded: () => {
    const { status } = get();
    if (status === "ready") return Promise.resolve();
    if (inflight) return inflight;

    set({ status: "loading" });
    inflight = (async () => {
      try {
        const { data } = await apiClient.get(API.AUTH.TENANT_CONTEXT);
        applyContext(set, data);
      } catch (error) {
        console.error("Failed to load tenant context:", error);
        // Keep whatever was seeded from cache; only the status flips to error.
        set({ status: "error" });
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  },

  // Force a re-fetch (ignores the ensureLoaded cache) so a saved tenant edit is
  // reflected live. A failed refresh keeps the currently-applied context rather
  // than blanking the app back to Oxygen defaults — it just logs.
  refresh: async () => {
    try {
      const { data } = await apiClient.get(API.AUTH.TENANT_CONTEXT);
      applyContext(set, data);
    } catch (error) {
      console.error("Failed to refresh tenant context:", error);
    }
  },
}));

/** Derived branding map ({} when no/invalid theme) — see parseThemeValues. */
export const selectBranding = (state) => parseThemeValues(state.theme);

/** Test helper: reset store + in-flight guard + cache to a pristine state. */
export const resetTenantContextStore = () => {
  inflight = null;
  try {
    globalThis.sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  useTenantContextStore.setState({
    status: "idle",
    name: null,
    theme: null,
    logoUrl: null,
    mediaUrl: null,
    authenticationMode: null,
  });
};

export default useTenantContextStore;
